import React, { createElement } from 'react';
import { XIcon, DownloadIcon, SendIcon, AlertTriangleIcon, PackageIcon } from 'lucide-react';
interface UrgentOrderItem {
  id: string;
  reference: string;
  design: string;
  currentStock: number;
  requiredStock: number;
  shortage: number;
  priority: 'critical' | 'high' | 'medium';
}
interface UrgentOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const mockUrgentItems: UrgentOrderItem[] = [{
  id: '1',
  reference: 'PVM-6-5/8-GALV-001',
  design: '6" 5/8 GALV',
  currentStock: 2,
  requiredStock: 15,
  shortage: 13,
  priority: 'critical'
}, {
  id: '2',
  reference: 'PVM-5-GALV-002',
  design: '5" GALV',
  currentStock: 2,
  requiredStock: 20,
  shortage: 18,
  priority: 'critical'
}, {
  id: '3',
  reference: 'LIN-STD-003',
  design: 'Linear',
  currentStock: 1,
  requiredStock: 15,
  shortage: 14,
  priority: 'critical'
}, {
  id: '4',
  reference: 'PVM-6-5/8-POLY-004',
  design: '6" 5/8 POLY',
  currentStock: 5,
  requiredStock: 17,
  shortage: 12,
  priority: 'high'
}, {
  id: '5',
  reference: 'PVM-8-5/8-GALV-005',
  design: '8" 5/8 GALV',
  currentStock: 8,
  requiredStock: 18,
  shortage: 10,
  priority: 'high'
}, {
  id: '6',
  reference: 'PVM-HP-STD-006',
  design: 'High Profile',
  currentStock: 12,
  requiredStock: 20,
  shortage: 8,
  priority: 'medium'
}];
export function UrgentOrdersModal({
  isOpen,
  onClose
}: UrgentOrdersModalProps) {
  if (!isOpen) return null;
  const handleDownloadExcel = () => {
    const headers = ['Reference', 'Design', 'Current Stock', 'Required Stock', 'Shortage', 'Priority'];
    const rows = mockUrgentItems.map(item => [item.reference, item.design, item.currentStock.toString(), item.requiredStock.toString(), item.shortage.toString(), item.priority.toUpperCase()]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `urgent-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const handlePlaceOrder = () => {
    alert('Factory order request initiated. This would integrate with your ERP system.');
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const totalShortage = mockUrgentItems.reduce((sum, item) => sum + item.shortage, 0);
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-605">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-606">
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 flex items-center justify-between" data-id="element-607">
          <div className="flex items-center gap-4" data-id="element-608">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-609">
              <AlertTriangleIcon className="w-6 h-6" data-id="element-610" />
            </div>
            <div data-id="element-611">
              <h2 className="text-2xl font-bold" data-id="element-612">Urgent Orders Required</h2>
              <p className="text-red-100 text-sm mt-1" data-id="element-613">
                {mockUrgentItems.length} references need immediate attention
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors" data-id="element-614">
            <XIcon className="w-6 h-6" data-id="element-615" />
          </button>
        </div>
        <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-616">
          <div className="flex items-center justify-between" data-id="element-617">
            <div className="flex items-center gap-6" data-id="element-618">
              <div className="text-center" data-id="element-619">
                <p className="text-sm text-gray-600 mb-1" data-id="element-620">Total Shortage</p>
                <p className="text-3xl font-bold text-red-600" data-id="element-621">
                  {totalShortage}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-622">machines</p>
              </div>
              <div className="w-px h-12 bg-gray-300" data-id="element-623"></div>
              <div className="text-center" data-id="element-624">
                <p className="text-sm text-gray-600 mb-1" data-id="element-625">Critical Items</p>
                <p className="text-3xl font-bold text-gray-900" data-id="element-626">
                  {mockUrgentItems.filter(i => i.priority === 'critical').length}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-627">
              <button onClick={handleDownloadExcel} className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-628">
                <DownloadIcon className="w-4 h-4" data-id="element-629" />
                Download Excel
              </button>
              <button onClick={handlePlaceOrder} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/30" data-id="element-630">
                <SendIcon className="w-4 h-4" data-id="element-631" />
                Place Factory Order
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6" data-id="element-632">
          <div className="space-y-3" data-id="element-633">
            {mockUrgentItems.map(item => <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all" data-id="element-634">
                <div className="flex items-start justify-between mb-4" data-id="element-635">
                  <div className="flex items-start gap-4 flex-1" data-id="element-636">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/30" data-id="element-637">
                      <PackageIcon className="w-6 h-6 text-white" data-id="element-638" />
                    </div>
                    <div className="flex-1" data-id="element-639">
                      <div className="flex items-center gap-3 mb-2" data-id="element-640">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-641">
                          {item.reference}
                        </h3>
                        <span className={`px-2.5 py-1 rounded text-xs font-semibold border ${getPriorityColor(item.priority)}`} data-id="element-642">
                          {item.priority.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-643">
                        {item.design}
                      </p>
                      <div className="grid grid-cols-4 gap-4" data-id="element-644">
                        <div data-id="element-645">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-646">
                            Current Stock
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-647">
                            {item.currentStock}
                          </p>
                        </div>
                        <div data-id="element-648">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-649">Required</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-650">
                            {item.requiredStock}
                          </p>
                        </div>
                        <div data-id="element-651">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-652">Shortage</p>
                          <p className="text-lg font-bold text-red-600" data-id="element-653">
                            {item.shortage}
                          </p>
                        </div>
                        <div data-id="element-654">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-655">Coverage</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-656">
                            {Math.round(item.currentStock / item.requiredStock * 100)}
                            %
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-4 border-t border-gray-100" data-id="element-657">
                  <button className="flex-1 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded transition-colors border border-blue-200 text-sm font-medium" data-id="element-658">
                    View Details
                  </button>
                  <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded hover:from-blue-600 hover:to-blue-700 transition-all text-sm font-medium shadow-sm" data-id="element-659">
                    Add to Order
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}